import type { CurrencyFormat } from "@rubyplay/effw-connector";
import { CaptainUpAdapter } from "@/integrations/captainup/CaptainUpAdapter";
import {
	bootCaptainUpOrThrow,
	type CaptainUpAuthMode,
	type CaptainUpBootContext,
} from "@/integrations/captainup/CaptainUpBoot";
import type { CaptainUpTheme, TooltipPosition } from "@/integrations/captainup/CaptainUpTypes";
import type {
	EngagementDockContentHandler,
	EngagementDockKind,
	EngagementModalContentHandler,
	EngagementModalKind,
	EngagementPauseResumeHandler,
	EngagementProvider,
	EngagementProviderCapabilities,
	EngagementProviderEventHandlerNormalized,
	EngagementProviderStatus,
	EngagementUserInteractionHandler,
	EngagementVendorEventHandler,
} from "@/provider/EngagementProvider";
import { debugLog, debugWarn } from "@/utils/debug";

function isCaptainUpModalKind(
	kind: EngagementDockKind | EngagementModalKind
): kind is "mission" | "tournament" {
	return kind === "mission" || kind === "tournament";
}

function otherCaptainUpModalKind(
	kind: "mission" | "tournament"
): "mission" | "tournament" {
	return kind === "mission" ? "tournament" : "mission";
}

function stableStringify(value: unknown): string {
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
}

function extractRewardKeysFromCampaign(entity: unknown): string[] {
	if (typeof entity === "string" || typeof entity === "number") {
		const key = String(entity).trim();

		return key.length > 0 ? [key] : [];
	}

	if (!entity || typeof entity !== "object" || Array.isArray(entity)) {
		return [];
	}

	const obj = entity as {
		id?: unknown;
	};

	if (obj.id !== undefined && obj.id !== null) {
		const key = String(obj.id).trim();

		return key.length > 0 ? [key] : [];
	}

	return [stableStringify(entity)];
}

function extractRewardKeysFromActivities(entity: unknown): string[] {
	if (!Array.isArray(entity)) {
		return [];
	}

	const keys: string[] = [];

	for (const item of entity) {
		const obj = item as {
			name?: unknown;
			entity?: {
				rewards?: unknown;
			};
		};

		if (obj?.name !== "achieve") {
			continue;
		}

		const rewards = obj.entity?.rewards;
		if (!Array.isArray(rewards) || rewards.length === 0) {
			continue;
		}

		for (const reward of rewards) {
			keys.push(stableStringify(reward));
		}
	}

	return keys;
}

function extractRewardKeysFromVendorEvent(
	vendorType: unknown,
	entity: unknown
): string[] {
	if (vendorType === "campaign") {
		return extractRewardKeysFromCampaign(entity);
	}

	if (vendorType === "activities") {
		return extractRewardKeysFromActivities(entity);
	}

	return [];
}

export class CaptainUpProvider implements EngagementProvider {
	public readonly id = "captainup";

	public readonly capabilities: EngagementProviderCapabilities = {
		supportsDock: true,
		supportsModal: true,
		supportsRewards: true,
		supportsBetSync: true,
		dockKinds: ["mission", "tournament"],
		modalKinds: ["mission", "tournament"],
	};

	private status: EngagementProviderStatus = "ready";
	private readonly adapter: CaptainUpAdapter;

	private constructor(adapter: CaptainUpAdapter) {
		this.adapter = adapter;

		debugLog("[CaptainUpProvider] constructed", {
			id: this.id,
			status: this.status,
			capabilities: this.capabilities,
		});
	}

	public static async bootOrThrow(
		authMode: CaptainUpAuthMode,
		theme?: CaptainUpTheme,
		modulesCsv?: string,
		ctx?: CaptainUpBootContext
	): Promise<CaptainUpProvider> {
		debugLog("[CaptainUpProvider] bootOrThrow() starting", {
			id: "captainup",
			gameName: ctx?.gameName ?? null,
			lang: ctx?.lang ?? null,
		});

		const dom = await bootCaptainUpOrThrow(authMode, theme, modulesCsv, ctx);
		const adapter = new CaptainUpAdapter(dom);

		return new CaptainUpProvider(adapter);
	}

	public getStatus(): EngagementProviderStatus {
		return this.status;
	}

	public attachDock(kind: EngagementDockKind, host: HTMLElement): void {
		if (!isCaptainUpModalKind(kind)) {
			debugWarn("[CaptainUpProvider] attachDock() ignored because kind is unsupported", { kind });
			return;
		}

		this.adapter.attachDockWidget(kind, host);
	}

	public parkDock(kind: EngagementDockKind, parkingHost: HTMLElement): void {
		if (!isCaptainUpModalKind(kind)) {
			debugWarn("[CaptainUpProvider] parkDock() ignored because kind is unsupported", { kind });
			return;
		}

		this.adapter.parkDockWidget(kind, parkingHost);
	}

	public parkAllDocks(parkingHost: HTMLElement): void {
		this.adapter.parkAllDockWidgets(parkingHost);
	}

	public attachModal(host: HTMLElement): void {
		this.adapter.attachModal(host);
	}

	public parkModal(parkingHost: HTMLElement): void {
		this.adapter.parkModal(parkingHost);
	}

	public openModal(kind: EngagementModalKind): void {
		this.adapter.openModal(kind);
	}

	public closeModal(kind: EngagementModalKind): void {
		this.adapter.closeModal(kind);
	}

	public ensureExclusiveModal(kind: EngagementModalKind): void {
		debugLog("[CaptainUpProvider] ensureExclusiveModal()", {
			kind,
			inactiveKind: otherCaptainUpModalKind(kind),
		});

		this.adapter.ensureExclusiveModal(kind);
	}

	public subscribe(
		handler: EngagementProviderEventHandlerNormalized
	): () => void {
		debugLog("[CaptainUpProvider] subscribe() normalized provider event stream");

		const unsubscribePauseResume = this.adapter.subscribePauseResume((type) => {
			const value = type === "cap.c2g.pause" ? "pause" : "resume";

			debugLog("[CaptainUpProvider] mapped pause/resume protocol event -> provider event", {
				protocolType: type,
				value,
			});

			handler({
				type: "pause_resume",
				value,
			});
		});

		const unsubscribeModalContent = this.adapter.subscribeModalContent(
			(hasContent) => {
				handler({ type: "modal_content_changed", hasContent });
			}
		);

		const unsubscribeDockContent = this.adapter.subscribeDockContent(
			(contentState) => {
				handler({
					type: "dock_content_changed",
					missionHasContent: contentState.missionHasContent,
					tournamentHasContent: contentState.tournamentHasContent,
				});
			}
		);

		const unsubscribeVendorEvents = this.adapter.subscribeEvents(
			(vendorType, entity) => {
				handler({
					type: "vendor_event",
					vendorType,
					entity,
				});

				if (vendorType === "snapshot") {
					debugWarn("[CaptainUpProvider] snapshot event received but vendor snapshot integration is intentionally stubbed", {
						vendorType,
						entity,
					});
					return;
				}

				const rewardKeys = extractRewardKeysFromVendorEvent(
					vendorType,
					entity
				);

				if (rewardKeys.length === 0) return;

				debugLog("[CaptainUpProvider] reward_acquired normalized from vendor event", {
					vendorType,
					rewardKeys,
				});

				handler({
					type: "reward_acquired",
					rewardKeys,
				});
			}
		);

		return () => {
			unsubscribePauseResume();
			unsubscribeModalContent();
			unsubscribeDockContent();
			unsubscribeVendorEvents();
		};
	}

	public subscribePauseResume(handler: EngagementPauseResumeHandler): () => void {
		return this.adapter.subscribePauseResume((type) => {
			handler(type === "cap.c2g.pause" ? "pause" : "resume");
		});
	}

	public subscribeUserInteraction(handler: EngagementUserInteractionHandler): () => void {
		return this.adapter.subscribeUserInteraction(handler);
	}

	public subscribeVendorEvents(handler: EngagementVendorEventHandler): () => void {
		return this.adapter.subscribeEvents(handler);
	}

	public subscribeModalContent(handler: EngagementModalContentHandler): () => void {
		return this.adapter.subscribeModalContent(handler);
	}

	public subscribeDockContent(handler: EngagementDockContentHandler): () => void {
		return this.adapter.subscribeDockContent(handler);
	}

	public updateBet(bet: number, currencyFormat?: unknown): void {
		this.adapter.updateBet(bet, currencyFormat as CurrencyFormat | undefined);
	}

	public notifyGameReady(): void {
		this.adapter.notifyGameReady();
	}

	public notifyPromotionStarted(): void {
		this.adapter.notifyPromotionStarted();
	}

	public notifyPromotionEnded(): void {
		this.adapter.notifyPromotionEnded();
	}

	public notifyBuyFeatureStarted(): void {
		this.adapter.notifyBuyFeatureStarted();
	}

	public notifyBuyFeatureEnded(): void {
		this.adapter.notifyBuyFeatureEnded();
	}

	public notifyPaidRoundStarted(): void {
		this.adapter.notifyPaidRoundStarted();
	}

	public notifyPaidRoundEnded(): void {
		this.adapter.notifyPaidRoundEnded();
	}

	public notifyRoundStarted(): void {
		this.adapter.notifyRoundStarted();
	}

	public notifyRoundEnded(): void {
		this.adapter.notifyRoundEnded();
	}

	public notifyAnimationsStarted(): void {
		this.adapter.notifyAnimationsStarted();
	}

	public notifyAnimationsEnded(): void {
		this.adapter.notifyAnimationsEnded();
	}

	public setTooltipPosition(position: TooltipPosition): void {
		this.adapter.setTooltipPosition(position);
	}
}